import {
  OmakasePolymorphicComponent,
  OmakasePolymorphicComponentProps,
  OmakasePolymorphicRootComponent,
} from '../utils/OmakasePolymorphicComponent';
import { Theme } from '@emotion/react';
import {
  listItemBase,
  listItemContent,
  listItemElement,
  listItemInteractiveElementEnd,
  listItemPrimaryText,
  listItemRoot,
  listItemSecondaryText,
  listItemSlimLeft,
  listItemSlimRight,
  listItemTall,
} from './listItemCssDefs';
import Anchor from 'components/Anchor';
import React from 'react';

export type ListItemCoreComponent = typeof Anchor | 'button';
export type ListItemCoreSize = 'medium' | 'large';
export type ListItemCoreVariant = 'basic';
export type ListItemCoreWeight = 'book' | 'medium';

export interface ListItemCoreBaseProps {
  /**
   * Whether or not the item is disabled
   */
  disabled?: boolean;
  /**
   * The href for an anchor component
   */
  href?: string;
  /**
   * An element before the content
   */
  elementStart?: React.ReactNode;
  /**
   * The click event. We may eventually want to make required when component is a button.
   */
  onClick?(e?: React.MouseEvent): void;
  /**
   * Optional extra text (lower hierarchical standing)
   */
  secondaryText?: string;
  /**
   * Whether or not the item is selected
   */
  selected?: boolean;
  /**
   * The size of the list item
   */
  size: ListItemCoreSize;
  /**
   * Whether or not the item's left side has slim padding.
   */
  slimLeft?: boolean;
  /**
   * Whether or not the item's right side has slim padding.
   */
  slimRight?: boolean;
  /**
   * Whether or not the item's vertical axis has increased padding.
   */
  tall?: boolean;
  /**
   * The functional variant for the item
   */
  variant: ListItemCoreVariant;
  /**
   * The weight of the primary text for list items
   */
  weight: ListItemCoreWeight;
  /**
   * The HTML element to render for the root element (usually an <li> component)
   */
  rootComponent?: 'li' | 'div';
}

type ElementEndStatic = {
  isInteractive?: false;
  element: React.ReactNode;
};

type ElementEndInteractive = {
  isInteractive: true;
  element: React.ReactNode;
  interactiveElementSize: number;
};

export type ListItemElementEndProps = ElementEndStatic | ElementEndInteractive;

export type ListItemCoreProps = ListItemCoreBaseProps & {
  elementEndProps?: ListItemElementEndProps;
};

const ListItemCoreDefaultElement = 'button';

export const ListItemCore: OmakasePolymorphicComponent<
  ListItemCoreProps,
  typeof ListItemCoreDefaultElement
> = React.forwardRef(
  <E extends React.ElementType = typeof ListItemCoreDefaultElement>(
    {
      children,
      component,
      disabled = false,
      href,
      elementEndProps,
      elementStart,
      onClick,
      secondaryText,
      selected = false,
      size,
      slimLeft = false,
      slimRight = false,
      tall = false,
      weight,
      rootComponent = 'li',
      ...rest
    }: OmakasePolymorphicComponentProps<ListItemCoreProps, E>,
    ref: React.Ref<HTMLButtonElement> | undefined
  ) => {
    const hasSecondaryText = Boolean(secondaryText);

    const sharedProps = {
      css: (theme: Theme) => [
        listItemBase(theme, {
          listItemSize: size,
          listItemWeight: weight,
          interactiveElementSize: elementEndProps?.isInteractive
            ? elementEndProps.interactiveElementSize
            : undefined,
          selected,
        }),
        slimLeft && listItemSlimLeft(theme),
        slimRight && listItemSlimRight(theme),
        tall && listItemTall(theme),
      ],
      onClick: disabled ? undefined : onClick,
      ...rest,
    };

    const RootComponent = rootComponent;
    const Component: React.ElementType = component || ListItemCoreDefaultElement;

    const listItemProps: { [key: string]: boolean | string | undefined } = {};
    if (Component === 'button') {
      listItemProps.disabled = disabled || undefined;
    } else {
      listItemProps['aria-disabled'] = disabled || undefined;
      listItemProps.href = disabled ? undefined : href;
    }

    const inner = (
      <>
        {elementStart && <span css={listItemElement}>{elementStart}</span>}
        <span css={listItemContent}>
          <span css={(theme: Theme) => listItemPrimaryText(theme, size, weight)}>{children}</span>
          {hasSecondaryText && (
            <span css={(theme: Theme) => listItemSecondaryText(theme)}>{secondaryText}</span>
          )}
        </span>
        {elementEndProps && !elementEndProps.isInteractive && (
          <span css={listItemElement}>{elementEndProps.element}</span>
        )}
      </>
    );

    return (
      <RootComponent css={listItemRoot}>
        <OmakasePolymorphicRootComponent
          ref={ref}
          component={Component}
          {...listItemProps}
          {...sharedProps}
        >
          {inner}
        </OmakasePolymorphicRootComponent>
        {elementEndProps?.isInteractive && (
          <span css={listItemInteractiveElementEnd}>{elementEndProps?.element}</span>
        )}
      </RootComponent>
    );
  }
);
